import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import type { FeedbackFormComponentConfig, FlowComponentRenderProps } from '../types';
import HtmlText from './HtmlText';

interface FeedbackValue {
  attachedRating?: RatingAttachment;
  notes?: string;
  submitted?: boolean;
  tags?: string[];
}

interface RatingAttachment {
  componentId: string;
  responseId?: string;
  score?: number;
  sentiment?: 'negative' | 'neutral' | 'positive';
}

const getFeedbackValue = (value: unknown): FeedbackValue => {
  if (typeof value === 'object' && value !== null) return value as FeedbackValue;

  return {};
};

const getRatingAttachment = (
  config: FeedbackFormComponentConfig,
  values: Record<string, unknown>
): RatingAttachment | undefined => {
  if (config.attachMode === 'none' || !config.attachToComponentId) return undefined;

  const ratingValue = values[config.attachToComponentId];
  if (typeof ratingValue !== 'object' || ratingValue === null) {
    return { componentId: config.attachToComponentId };
  }

  const rating = ratingValue as {
    componentId?: unknown;
    responseId?: unknown;
    score?: unknown;
    sentiment?: unknown;
  };

  return {
    componentId:
      typeof rating.componentId === 'string' ? rating.componentId : config.attachToComponentId,
    responseId: typeof rating.responseId === 'string' ? rating.responseId : undefined,
    score: typeof rating.score === 'number' ? rating.score : undefined,
    sentiment:
      rating.sentiment === 'negative' || rating.sentiment === 'neutral' || rating.sentiment === 'positive'
        ? rating.sentiment
        : undefined,
  };
};

const mergeRelatedFeedback = (
  config: FeedbackFormComponentConfig,
  values: Record<string, unknown>,
  feedbackValue: FeedbackValue,
  selectedTags: string[]
) => {
  const relatedValues = (config.relatedFeedbackComponentIds ?? []).map((componentId) =>
    getFeedbackValue(values[componentId])
  );
  const relatedNotes = relatedValues
    .map((relatedValue) => relatedValue.notes)
    .filter((notes): notes is string => Boolean(notes?.trim()));
  const tags = Array.from(
    new Set([
      ...relatedValues.flatMap((relatedValue) => relatedValue.tags ?? []),
      ...selectedTags,
    ])
  );

  return {
    notes: [...relatedNotes, feedbackValue.notes ?? ''].filter((notes) => notes.trim()).join('\n\n'),
    tags,
  };
};

const FeedbackFormBlock: React.FC<FlowComponentRenderProps<FeedbackFormComponentConfig>> = ({
  config,
  setValue,
  submitValueAndGoNext,
  value,
  values,
}) => {
  const feedbackValue = getFeedbackValue(value);
  const selectedTags = feedbackValue.tags ?? [];
  const attachedRating = getRatingAttachment(config, values);
  const submitted = feedbackValue.submitted === true;
  const allowResubmit = config.allowResubmit ?? true;
  const locked = submitted && !allowResubmit;
  const showSubmitAction = config.showSubmitAction === true;
  const inputMode = config.inputMode ?? 'tagsAndText';
  const showTags = inputMode !== 'text' && Boolean(config.tags?.length);
  const showTextInput = inputMode !== 'tags';

  const toggleTag = (tag: string) => {
    if (locked) return;

    const nextTags = selectedTags.includes(tag)
      ? selectedTags.filter((selectedTag) => selectedTag !== tag)
      : [...selectedTags, tag];

    setValue(config.id, { ...feedbackValue, attachedRating, submitted: false, tags: nextTags });
  };

  return (
    <Box
      sx={{
        bgcolor: 'var(--cx-brand-surface)',
        border: config.style === 'plain' ? 0 : '1px solid',
        borderColor: config.style === 'plain' ? 'transparent' : 'var(--cx-brand-border)',
        borderRadius: config.style === 'plain' ? 0 : 2,
        color: 'var(--cx-brand-text)',
        maxWidth: config.maxWidth ?? 'none',
        mx: config.maxWidth ? 'auto' : 0,
        p: config.style === 'plain' ? 0 : 2,
        width: '100%',
      }}
    >
      <Stack spacing={1.5}>
        {config.title ? (
          <HtmlText
            component="div"
            html={config.title}
            variant="subtitle2"
            sx={{ textAlign: config.align ?? 'left' }}
          />
        ) : null}

        {attachedRating && config.style !== 'plain' ? (
          <Box
            sx={{
              bgcolor: 'var(--cx-brand-primary-soft)',
              border: '1px solid var(--cx-brand-border)',
              borderRadius: 2,
              p: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'var(--cx-brand-primary-dark)', fontWeight: 900 }}>
              Attached to rating
              {typeof attachedRating.score === 'number' ? ` · ${attachedRating.score}` : ''}
              {attachedRating.sentiment ? ` · ${attachedRating.sentiment}` : ''}
            </Typography>
          </Box>
        ) : null}

        {showTags ? (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flexWrap: 'wrap',
              justifyContent: config.align === 'center' ? 'center' : 'flex-start',
              rowGap: 0.75,
            }}
          >
            {config.tags!.map((tag) => {
              const selected = selectedTags.includes(tag);

              return (
                <Chip
                  key={tag}
                  label={tag}
                  disabled={locked}
                  onClick={() => toggleTag(tag)}
                  sx={{
                    bgcolor: selected ? 'var(--cx-brand-button-selected-bg)' : 'var(--cx-brand-button-bg)',
                    border: '1px solid',
                    borderColor: selected
                      ? 'var(--cx-brand-button-selected-border)'
                      : 'var(--cx-brand-button-border)',
                    color: selected ? 'var(--cx-brand-button-selected-text)' : 'var(--cx-brand-button-text)',
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: selected
                        ? 'var(--cx-brand-button-selected-bg)'
                        : 'var(--cx-brand-button-hover-bg)',
                    },
                  }}
                />
              );
            })}
          </Stack>
        ) : null}

        {showTextInput ? (
          <TextField
            disabled={locked}
            fullWidth
            multiline
            minRows={3}
            onChange={(event) =>
              setValue(config.id, {
                ...feedbackValue,
                attachedRating,
                notes: event.target.value,
                submitted: false,
              })
            }
            placeholder={config.placeholder ?? 'Add details'}
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: 'var(--cx-brand-bg)',
                color: 'var(--cx-brand-text)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--cx-brand-border)',
              },
            }}
            value={feedbackValue.notes ?? ''}
          />
        ) : null}

        {showSubmitAction && submitted ? (
          <Card
            sx={{
              bgcolor: 'var(--cx-brand-primary-soft)',
              border: '1px solid var(--cx-brand-border)',
              boxShadow: 'none',
              p: 1.25,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 900 }}>
              {config.submittedTitle ?? 'Feedback already submitted'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', mt: 0.35 }}>
              {config.submittedBody ??
                (allowResubmit
                  ? 'You can update your response or continue to the next step.'
                  : 'Your previous response is locked for this visit.')}
            </Typography>
          </Card>
        ) : null}

        {showSubmitAction ? (
          <Button
            fullWidth
            onClick={() =>
              submitValueAndGoNext(config.id, {
                ...mergeRelatedFeedback(config, values, feedbackValue, selectedTags),
                attachedRating,
                submitted: true,
              })
            }
            sx={{
              bgcolor: 'var(--cx-brand-button-selected-bg)',
              border: '1px solid',
              borderColor: 'var(--cx-brand-button-selected-border)',
              color: 'var(--cx-brand-button-selected-text)',
              fontWeight: 900,
              py: 1.15,
              '&:hover': { bgcolor: 'var(--cx-brand-button-hover-bg)' },
            }}
            type="button"
            variant="contained"
          >
            {submitted ? config.submittedActionLabel ?? 'Continue' : config.actionLabel ?? 'Submit and continue'}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default FeedbackFormBlock;
